import { useState, useEffect } from 'react';

const MODE_LABELS = { standard: 'Standard', courtesy: 'Courtesy', location: 'Location', takeback: 'Take Back' };
const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Admin', dispatcher: 'Dispatcher', sales: 'Sales', driver: 'Driver' };

function MultiSelectCheckboxes({ field, value, onChange }) {
  const selected = value ? value.split(',').filter(Boolean) : [];
  function toggle(optValue) {
    const newSelected = selected.includes(optValue)
      ? selected.filter(v => v !== optValue)
      : [...selected, optValue];
    onChange(newSelected.join(','));
  }
  return (
    <div className="multiselect-checkboxes">
      <label className="multiselect-label">{field.label}</label>
      <div className="multiselect-options">
        {field.options.map(opt => (
          <label key={opt.value} className="multiselect-option">
            <input
              type="checkbox"
              checked={selected.includes(opt.value)}
              onChange={() => toggle(opt.value)}
            />
            <span>{opt.label}</span>
          </label>
        ))}
      </div>
      {selected.length === 0 && <div className="text-muted" style={{fontSize: '0.8rem', marginTop: 2}}>All roles can use this type</div>}
    </div>
  );
}

export default function ConfigListManager({ title, fetchFn, createFn, updateFn, deleteFn, extraFields }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newName, setNewName] = useState('');
  const [newExtras, setNewExtras] = useState({});
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editExtras, setEditExtras] = useState({});
  const [error, setError] = useState('');

  // Initialize default values for extra fields
  useEffect(() => {
    if (extraFields) {
      const defaults = {};
      for (const field of extraFields) {
        defaults[field.key] = field.defaultValue || '';
      }
      setNewExtras(defaults);
    }
  }, []); // eslint-disable-line

  async function load() {
    try { setItems(await fetchFn()); }
    catch (err) { setError(err.message); }
    setLoading(false);
  }

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function handleCreate(e) {
    e.preventDefault();
    if (!newName.trim()) return;
    setError('');
    try {
      const data = { name: newName.trim(), ...newExtras };
      await createFn(data);
      setNewName('');
      // Reset extras to defaults
      if (extraFields) {
        const defaults = {};
        for (const field of extraFields) defaults[field.key] = field.defaultValue || '';
        setNewExtras(defaults);
      }
      load();
    } catch (err) { setError(err.message); }
  }

  async function handleUpdate(id) {
    if (!editName.trim()) return;
    setError('');
    try {
      const data = { name: editName.trim(), ...editExtras };
      await updateFn(id, data);
      setEditId(null);
      load();
    } catch (err) { setError(err.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this item?')) return;
    setError('');
    try { await deleteFn(id); load(); }
    catch (err) { setError(err.message); }
  }

  function startEdit(item) {
    setEditId(item.id);
    setEditName(item.name);
    if (extraFields) {
      const extras = {};
      for (const field of extraFields) extras[field.key] = item[field.key] || field.defaultValue || '';
      setEditExtras(extras);
    }
  }

  function renderField(field, extras, setExtras) {
    if (field.type === 'select') {
      return (
        <select key={field.key} value={extras[field.key] || ''} onChange={e => setExtras({...extras, [field.key]: e.target.value})}>
          {field.options.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
        </select>
      );
    }
    if (field.type === 'multiselect') {
      return (
        <MultiSelectCheckboxes
          key={field.key}
          field={field}
          value={extras[field.key] || ''}
          onChange={val => setExtras({...extras, [field.key]: val})}
        />
      );
    }
    return null;
  }

  function renderBadges(item) {
    if (!extraFields) return null;
    return extraFields.map(field => {
      if (field.type === 'select' && item[field.key] && item[field.key] !== field.defaultValue) {
        return <span key={field.key} className="config-mode-badge">{MODE_LABELS[item[field.key]] || item[field.key]}</span>;
      }
      if (field.type === 'multiselect' && item[field.key]) {
        const roles = item[field.key].split(',').filter(Boolean);
        if (roles.length === 0) return null;
        return (
          <span key={field.key} className="config-roles-badge">
            {roles.map(r => ROLE_LABELS[r] || r).join(', ')}
          </span>
        );
      }
      return null;
    });
  }

  if (loading) return <div className="loading">Loading...</div>;

  return (
    <div className="config-list-manager">
      <h2>{title}</h2>
      {error && <div className="alert alert-error">{error}</div>}

      <form className="config-add-form" onSubmit={handleCreate}>
        <input value={newName} onChange={e => setNewName(e.target.value)} placeholder={`New ${title.replace(/s$/, '').toLowerCase()}...`} />
        {extraFields && extraFields.map(field => renderField(field, newExtras, setNewExtras))}
        <button type="submit" className="btn btn-primary">Add</button>
      </form>

      <div className="config-list">
        {items.length === 0 ? (
          <div className="empty-state">No items yet. Add one above.</div>
        ) : (
          items.map(item => (
            <div key={item.id} className="config-item">
              {editId === item.id ? (
                <div className="config-edit">
                  <input value={editName} onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') handleUpdate(item.id); if (e.key === 'Escape') setEditId(null); }}
                    autoFocus />
                  {extraFields && extraFields.map(field => renderField(field, editExtras, setEditExtras))}
                  <button className="btn btn-sm btn-primary" onClick={() => handleUpdate(item.id)}>Save</button>
                  <button className="btn btn-sm btn-muted" onClick={() => setEditId(null)}>Cancel</button>
                </div>
              ) : (
                <div className="config-display">
                  <span className="config-name">
                    {item.name}
                    {renderBadges(item)}
                  </span>
                  <div className="config-actions">
                    <button className="btn btn-sm btn-muted" onClick={() => startEdit(item)}>Edit</button>
                    <button className="btn btn-sm btn-danger" onClick={() => handleDelete(item.id)}>Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
